import React from 'react';
import { useDrag } from 'react-dnd';
import { ItemTypes } from '../utils/dragDropTypes';

import { X, Type, Image as ImageIcon, Music, FileText, MessageSquare, Video, Zap } from 'lucide-react';

const TimelineClip = ({ clip, layerId, layerType, layerName, layerCategory, onMove, onResize, onRemove, onUpdateClip, pixelsPerSecond, onSelectClip, onSeek }) => {
    const isSfx = layerCategory === 'soundEffect';

    const getLayerIcon = () => {
        const type = (layerType || '').toLowerCase();
        const name = (layerName || '').toLowerCase();
        const iconClass = `w-4 h-4 ${isSfx ? 'text-gray-900' : 'text-white'}`;
        switch (type) {
            case 'video':
                return <Video className={iconClass} />;
            case 'image':
                return <ImageIcon className={iconClass} />;
            case 'audio':
                // Sound effects get a Zap icon; music keeps the Music note
                return isSfx
                    ? <Zap className={iconClass} />
                    : <Music className={iconClass} />;
            case 'text':
                if (name.includes('caption')) return <MessageSquare className={iconClass} />;
                return <Type className={iconClass} />;
            case 'captions':
                return <MessageSquare className={iconClass} />;
            default:
                return <FileText className={iconClass} />;
        }
    };
    const [{ isDragging }, drag] = useDrag(() => ({
        type: ItemTypes.TIMELINE_CLIP,
        item: { clip, layerId },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    }));

    const leftPosition = clip.startTime * pixelsPerSecond;
    const width = clip.duration * pixelsPerSecond;

    const getClipColor = () => {
        const type = (clip.mediaFile?.type || clip.type || layerType || "").toLowerCase();
        const isCaption = layerId?.toLowerCase().includes('caption') || type === 'captions' || (layerName && layerName.toLowerCase().includes('caption'));

        if (type.startsWith('video') || type === 'video') {
            return 'bg-gradient-to-r from-blue-600/90 to-blue-700/90';
        }
        if (type.startsWith('audio') || type === 'audio') {
            // Sound effects: bright vivid yellow/amber - as requested #FFC107
            if (isSfx) return 'bg-[#FFC107]';
            // Music: warm orange
            return 'bg-gradient-to-r from-orange-600/90 to-orange-700/90';
        }
        if (type.startsWith('image') || type === 'image') {
            return 'bg-gradient-to-r from-purple-600/90 to-purple-700/90';
        }
        if (type === 'text' || type === 'text/plain' || isCaption) {
            return 'bg-gradient-to-r from-green-600/90 to-green-700/90';
        }
        return 'bg-gradient-to-r from-gray-600/90 to-gray-700/90';
    };

    const isAudio = (clip.mediaFile?.type || "").toLowerCase().includes('audio');

    // ... (rest of the file until the render) ...
    // (We need to reach the render part to change the text class)
    // Since replace_file_content works on contiguous blocks, I might need two calls or one larger block.
    // The getClipColor is at the top (around line 19)
    // The render is at the bottom (around line 160)
    // I'll do two edits. This tool call is for getClipColor.


    // Handle resizing
    const handleResizeStart = (e, direction) => {
        e.stopPropagation();
        e.preventDefault();

        const startX = e.clientX;
        const pointerId = e.pointerId;
        const startStartTime = clip.startTime;
        const startDuration = clip.duration;
        const startTrimStart = clip.trimStart || 0;

        const handlePointerMove = (moveEvent) => {
            if (moveEvent.pointerId !== pointerId) return;
            const deltaX = moveEvent.clientX - startX;
            const deltaTime = deltaX / pixelsPerSecond;

            if (direction === 'left') {
                // Dragging left handle:
                // - startTime changes (+deltaTime)
                // - duration changes (-deltaTime)
                // - trimStart changes (+deltaTime)

                let newStartTime = startStartTime + deltaTime;
                let newDuration = startDuration - deltaTime;
                let newTrimStart = startTrimStart + deltaTime;

                // Constraints
                // 1. Duration must be > 0.1s
                if (newDuration < 0.1) {
                    newDuration = 0.1;
                    newStartTime = startStartTime + (startDuration - 0.1);
                    newTrimStart = startTrimStart + (startDuration - 0.1);
                }

                // 2. trimStart must be >= 0
                if (newTrimStart < 0) {
                    newTrimStart = 0;
                    const diff = 0 - startTrimStart; // negative
                    newStartTime = startStartTime + diff;
                    newDuration = startDuration - diff;
                }

                // 3. startTime must be >= 0
                if (newStartTime < 0) {
                    newStartTime = 0;
                }

                onUpdateClip(layerId, clip.id, {
                    startTime: newStartTime,
                    duration: newDuration,
                    trimStart: newTrimStart
                });

            } else {
                // Dragging right handle:
                // - duration changes (+deltaTime)
                // - startTime stays same
                // - trimStart stays same

                let newDuration = startDuration + deltaTime;

                // Constraints
                // 1. Duration must be > 0.1s
                if (newDuration < 0.1) newDuration = 0.1;

                // 2. Cannot exceed media duration (if known) - trimStart
                // Only enforce this for video and audio. Text/Image can be infinite.
                const clipType = clip.mediaFile?.type || '';
                const isStaticMedia = clipType === 'text' ||
                    clipType === 'text/plain' ||
                    clipType.startsWith('image/') ||
                    !clip.mediaFile;

                if (!isStaticMedia && clip.mediaFile) {
                    const maxDuration = (clip.mediaFile.duration || 1000) - startTrimStart;
                    if (newDuration > maxDuration) {
                        newDuration = maxDuration;
                    }
                }

                onUpdateClip(layerId, clip.id, {
                    duration: newDuration
                });
            }
        };

        const handlePointerUp = (upEvent) => {
            if (upEvent.pointerId !== pointerId) return;
            document.removeEventListener('pointermove', handlePointerMove);
            document.removeEventListener('pointerup', handlePointerUp);
            document.removeEventListener('pointercancel', handlePointerUp);
        };

        document.addEventListener('pointermove', handlePointerMove);
        document.addEventListener('pointerup', handlePointerUp);
        document.addEventListener('pointercancel', handlePointerUp);
    };

    return (
        <div
            className={`clip ${getClipColor()} ${isDragging ? 'opacity-20' : ''} group rounded-[5px] shadow-sm overflow-hidden flex items-center`}
            style={{
                left: `${leftPosition}px`,
                width: `${width}px`,
                height: '36px',
                top: '6px', // Centered in h-12 (48px)
            }}
            onClick={(e) => {
                e.stopPropagation();
                if (onSelectClip) onSelectClip(clip.id);
                if (onSeek) onSeek(clip.startTime + 0.01);
            }}
        >
            {/* Audio Waveform visualization - WhatsApp Style Bars */}


            {/* Thumbnail */}
            {clip.mediaFile?.thumbnail && (
                <div className="absolute inset-0 opacity-30 pointer-events-none">
                    <img
                        src={clip.mediaFile.thumbnail}
                        crossOrigin="anonymous"
                        alt=""
                        className="w-full h-full object-cover"
                    />
                </div>
            )}




            {/* Clip Info */}
            <div ref={drag} className="flex-1 flex items-center px-1 overflow-hidden h-full cursor-grab active:cursor-grabbing" style={{ touchAction: 'none' }}>
                {/* Icon Container - precisely w-8 h-8 matching sample */}
                <div className="w-8 h-8 rounded-md flex items-center justify-center mr-3 shrink-0 bg-white/20 dark:bg-gray-800/60">
                    {getLayerIcon()}
                </div>

                {/* Text Content */}
                <span className={`text-sm font-medium ${isSfx ? 'text-gray-900' : 'text-white/90'} truncate mr-2`}>
                    {clip.text || clip.mediaFile?.name || (layerType === 'text' ? 'Text' : 'Caption')}
                </span>

                {isAudio && (
                    <div
                        className="flex-1 h-full opacity-40 ml-1"
                        style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 40' preserveAspectRatio='none'%3E%3Crect x='2' y='12' width='3' height='16' rx='1.5' fill='${isSfx ? 'black' : 'white'}' /%3E%3Crect x='7' y='8' width='3' height='24' rx='1.5' fill='${isSfx ? 'black' : 'white'}' /%3E%3Crect x='12' y='14' width='3' height='12' rx='1.5' fill='${isSfx ? 'black' : 'white'}' /%3E%3Crect x='17' y='5' width='3' height='30' rx='1.5' fill='${isSfx ? 'black' : 'white'}' /%3E%3Crect x='22' y='10' width='3' height='20' rx='1.5' fill='${isSfx ? 'black' : 'white'}' /%3E%3Crect x='27' y='15' width='3' height='10' rx='1.5' fill='${isSfx ? 'black' : 'white'}' /%3E%3Crect x='32' y='8' width='3' height='24' rx='1.5' fill='${isSfx ? 'black' : 'white'}' /%3E%3Crect x='37' y='2' width='3' height='36' rx='1.5' fill='${isSfx ? 'black' : 'white'}' /%3E%3Crect x='42' y='12' width='3' height='16' rx='1.5' fill='${isSfx ? 'black' : 'white'}' /%3E%3Crect x='47' y='6' width='3' height='28' rx='1.5' fill='${isSfx ? 'black' : 'white'}' /%3E%3Crect x='52' y='14' width='3' height='12' rx='1.5' fill='${isSfx ? 'black' : 'white'}' /%3E%3Crect x='57' y='10' width='3' height='20' rx='1.5' fill='${isSfx ? 'black' : 'white'}' /%3E%3Crect x='62' y='16' width='3' height='8' rx='1.5' fill='${isSfx ? 'black' : 'white'}' /%3E%3Crect x='67' y='8' width='3' height='24' rx='1.5' fill='${isSfx ? 'black' : 'white'}' /%3E%3Crect x='72' y='4' width='3' height='32' rx='1.5' fill='${isSfx ? 'black' : 'white'}' /%3E%3Crect x='77' y='12' width='3' height='16' rx='1.5' fill='${isSfx ? 'black' : 'white'}' /%3E%3Crect x='82' y='14' width='3' height='12' rx='1.5' fill='${isSfx ? 'black' : 'white'}' /%3E%3Crect x='87' y='10' width='3' height='20' rx='1.5' fill='${isSfx ? 'black' : 'white'}' /%3E%3Crect x='92' y='15' width='3' height='10' rx='1.5' fill='${isSfx ? 'black' : 'white'}' /%3E%3Crect x='97' y='12' width='3' height='16' rx='1.5' fill='${isSfx ? 'black' : 'white'}' /%3E%3C/svg%3E")`,
                            backgroundRepeat: 'repeat-x',
                            backgroundSize: '100px 80%',
                            backgroundPosition: 'left center'
                        }}
                    />
                )}
            </div>

            <button
                className="absolute top-0.5 right-0.5 p-0.5 rounded-full hover:bg-black/40 text-white/70 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity z-30"
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove(layerId, clip.id);
                }}
                title="Remove clip"
            >
                <X className="w-3 h-3" />
            </button>

            {/* Resize Handles */}
            <div
                className="video-editor-clip-resize-handle absolute left-0 top-0 bottom-0 w-2 bg-white/0 hover:bg-white/50 cursor-ew-resize z-20 transition-colors"
                onPointerDown={(e) => handleResizeStart(e, 'left')}
                onTouchStart={(e) => e.stopPropagation()}
                style={{ touchAction: 'none' }}
            />
            <div
                className="video-editor-clip-resize-handle absolute right-0 top-0 bottom-0 w-2 bg-white/0 hover:bg-white/50 cursor-ew-resize z-20 transition-colors"
                onPointerDown={(e) => handleResizeStart(e, 'right')}
                onTouchStart={(e) => e.stopPropagation()}
                style={{ touchAction: 'none' }}
            />
        </div>
    );
};

export default TimelineClip;
